import { useState, useEffect, useCallback } from 'react';
import { utils, read } from 'xlsx';
import apiClient from '../../../hooks/apiClient';
import { useGetLifeInsuranceData, useImportLifeInsuranceData } from '../../../hooks/useContracts';
import { usePlans } from '../../../hooks/usePlans';
import { useGroups } from '../../../hooks/useGroups';
import { downloadExcel, downloadExcelForInsurer } from '../utils/lifeInsuranceUtils';
import { ITEMS_PER_PAGE } from '../constants/lifeInsuranceConstants';

export const useLifeInsurance = () => {
  const { data: plans = [] } = usePlans();
  const { data: groups = [] } = useGroups();

  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState({
    plan: '',
    group: '',
    cuotaPura: '',
  });
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);

  const [selectedFile, setSelectedFile] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const lifeInsuranceMutation = useGetLifeInsuranceData();
  const importMutation = useImportLifeInsuranceData();

  const fetchData = useCallback(async () => {
    if (!filter.cuotaPura) {
      setData([]);
      setTotalPages(0);
      setTotalCount(0);
      return;
    }

    try {
      const params = {
        cuota_pura: filter.cuotaPura,
        page,
        per_page: rowsPerPage,
        ...(filter.plan && { plan: filter.plan }),
        ...(filter.group && { group: filter.group }),
      };

      const response = await lifeInsuranceMutation.mutateAsync(params);
      setData(response.data || []);
      setTotalPages(response.pages || 0);
      setTotalCount(response.total || 0);
      if (response.pages && page > response.pages) {
        setPage(Math.max(1, response.pages));
      }
    } catch (error) {
      console.error('Error fetching life insurance data:', error);
      setData([]);
      setTotalPages(0);
      setTotalCount(0);
    }
  }, [page, rowsPerPage, filter, lifeInsuranceMutation]);

  useEffect(() => {
    if (filter.cuotaPura) {
      fetchData();
    }
  }, [page, rowsPerPage]); //! NO MODIFICAR ESTO SINO TENGO RENDER INFINITO

  const handleFilter = useCallback((event) => {
    const { name, value } = event.target;
    setFilter((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleSearch = useCallback(() => {
    if (filter.cuotaPura) {
      setPage(1);
      fetchData();
    }
  }, [filter.cuotaPura, fetchData]);

  const handlePageChange = useCallback((event, value) => {
    setPage(value);
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    const value = parseInt(event.target.value, 10);
    if (!Number.isNaN(value)) {
      setRowsPerPage(value);
      setPage(1);
    }
  }, []);

  const handleDownloadExcel = useCallback(async () => {
    if (!filter.cuotaPura) {
      return;
    }

    try {
      const params = {
        cuota_pura: filter.cuotaPura,
        page: 1,
        per_page: 1000,
        ...(filter.plan && { plan: filter.plan }),
        ...(filter.group && { group: filter.group }),
      };

      const response = await lifeInsuranceMutation.mutateAsync(params);
      downloadExcel(response.data || []);
    } catch (error) {
      console.error('Error downloading excel:', error);
    }
  }, [filter, lifeInsuranceMutation]);

  const handleDownloadExcelForInsurer = useCallback(async () => {
    if (!filter.cuotaPura) {
      return;
    }

    try {
      const params = {
        cuota_pura: filter.cuotaPura,
        page: 1,
        per_page: 1000,
        ...(filter.plan && { plan: filter.plan }),
        ...(filter.group && { group: filter.group }),
      };

      const response = await lifeInsuranceMutation.mutateAsync(params);
      const lifeInsuranceData = response.data || [];

      if (lifeInsuranceData.length === 0) {
        return;
      }

      const contractsResponse = await apiClient.get('contracts/with-subscribers');
      const subscribersMap = (contractsResponse.data || []).reduce((acc, contract) => {
        const contractNumber = String(contract.contract_number);
        acc[contractNumber] = (contract.subscribers || []).map(
          (subscriber) => `${subscriber.last_name}, ${subscriber.first_name}`
        );
        return acc;
      }, {});

      const dataForInsurer = lifeInsuranceData.flatMap((item) => {
        const contractNumber = String(item.order);
        const subscribers =
          subscribersMap[contractNumber] || (item.subscriber ? [item.subscriber] : []);
        const subscribersCount = subscribers.length || 1;
        const lifeInsuranceAmount = Number(item.lifeInsurance) || 0;
        const lifeInsurancePerSubscriber = Number(
          (lifeInsuranceAmount / subscribersCount).toFixed(2)
        );

        if (subscribers.length === 0) {
          return [
            {
              ...item,
              subscriber: 'Sin suscriptor',
              lifeInsurance: lifeInsurancePerSubscriber,
            },
          ];
        }

        return subscribers.map((subscriberName) => ({
          ...item,
          subscriber: subscriberName,
          lifeInsurance: lifeInsurancePerSubscriber,
        }));
      });

      downloadExcelForInsurer(dataForInsurer);
    } catch (error) {
      console.error('Error downloading insurer excel:', error);
    }
  }, [filter, lifeInsuranceMutation]);

  const handleFileUpload = useCallback((event) => {
    setSelectedFile(event.target.files[0]);
  }, []);

  const readExcel = useCallback((file) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = read(data, { type: 'array' });
      const worksheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[worksheetName];
      let json = utils.sheet_to_json(worksheet, { header: 1 });
      json = json.map((row) => [row[2], row[0], row[1], row[7]]);
      setTableData(json);
    };
    reader.readAsArrayBuffer(file);
  }, []);

  const handleImportValues = useCallback(() => {
    if (!selectedFile) {
      setSnackbar({
        open: true,
        message: 'Es necesario seleccionar un archivo',
        severity: 'error',
      });
      return;
    }
    readExcel(selectedFile);
    setImportDialogOpen(true);
  }, [selectedFile, readExcel]);

  const handleCloseImportDialog = useCallback(() => {
    setImportDialogOpen(false);
  }, []);

  const handleUpload = useCallback(async () => {
    if (!selectedFile) {
      return;
    }

    try {
      const response = await importMutation.mutateAsync(selectedFile);

      setImportDialogOpen(false);
      setSnackbar({
        open: true,
        message: `Importación completada: ${response.statistics.updated} contratos actualizados`,
        severity: 'success',
      });

      setSelectedFile(null);
      setTableData([]);

      if (filter.cuotaPura) {
        fetchData();
      }
    } catch (error) {
      console.error('Error importing data:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Error al importar datos',
        severity: 'error',
      });
    }
  }, [selectedFile, importMutation, filter.cuotaPura, fetchData]);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  return {
    data,
    isLoading: lifeInsuranceMutation.isPending || importMutation.isPending,
    page,
    totalPages,
    totalCount,
    rowsPerPage,
    filter,
    plans,
    groups,
    handleFilter,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleDownloadExcel,
    handleDownloadExcelForInsurer,
    selectedFile,
    tableData,
    importDialogOpen,
    importLoading: importMutation.isPending,
    snackbar,
    handleFileUpload,
    handleImportValues,
    handleCloseImportDialog,
    handleUpload,
    handleCloseSnackbar,
  };
};
